import { useCallback, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

export type UrlParamValue =
    | string
    | number
    | number[]
    | string[]
    | undefined
    | null;

export interface UrlParams {
    getString: (name: string) => string | undefined;
    getNumber: (name: string) => number | undefined;
    getNumberArray: (name: string) => number[] | undefined;
    /**
     * Merges updates into the query string. Pass undefined or null to remove a param.
     * Uses history.replace, so typing in a filter does not fill the back stack with an entry per
     * keystroke, while still leaving the state on the URL for reload and for sharing.
     */
    setParams: (updates: Record<string, UrlParamValue>) => void;
}

/**
 * Reads and writes component state on the query string.
 *
 * This is what keeps filter and paging state alive when the user switches between admin endpoint
 * cards, which unmount their children, and it makes a filtered view a shareable link.
 */
export const useUrlParams = (): UrlParams => {
    const history = useHistory();
    const location = useLocation();

    const search = useMemo(
        () => new URLSearchParams(location.search),
        [location.search]
    );

    const getString = useCallback(
        (name: string): string | undefined => {
            const value = search.get(name);
            return value === null || value === '' ? undefined : value;
        },
        [search]
    );

    const getNumber = useCallback(
        (name: string): number | undefined => {
            const value = search.get(name);
            if (value === null || value.trim() === '') {
                return undefined;
            }
            const parsed = Number(value);
            // A malformed param should fall back to the default rather than poison the query.
            return Number.isFinite(parsed) ? parsed : undefined;
        },
        [search]
    );

    const getNumberArray = useCallback(
        (name: string): number[] | undefined => {
            const values = search.getAll(name);
            if (values.length === 0) {
                return undefined;
            }
            const parsed = values
                .flatMap((v) => v.split(','))
                .map((v) => Number(v))
                .filter((v) => Number.isFinite(v));
            return parsed.length > 0 ? parsed : undefined;
        },
        [search]
    );

    const setParams = useCallback(
        (updates: Record<string, UrlParamValue>) => {
            const next = new URLSearchParams(location.search);

            Object.entries(updates).forEach(([name, value]) => {
                next.delete(name);

                if (value === undefined || value === null || value === '') {
                    return;
                }

                if (Array.isArray(value)) {
                    if (value.length > 0) {
                        next.set(name, value.join(','));
                    }
                    return;
                }

                next.set(name, String(value));
            });

            history.replace({
                pathname: location.pathname,
                search: next.toString(),
            });
        },
        [history, location.pathname, location.search]
    );

    return { getString, getNumber, getNumberArray, setParams };
};

/**
 * State backed by sessionStorage, for things too large or too transient for the URL such as
 * fetched results and expansion state. Survives an endpoint card swap and a page reload, and is
 * discarded when the tab closes.
 */
export const useSessionState = <T>(
    key: string,
    initialValue: T
): [T, (value: T) => void] => {
    const readValue = (): T => {
        try {
            const stored = window.sessionStorage.getItem(key);
            return stored === null ? initialValue : (JSON.parse(stored) as T);
        } catch {
            // Corrupt or unreadable storage should never break the page.
            return initialValue;
        }
    };
    const [entry, setEntry] = useState(() => ({ key, value: readValue() }));
    const value = entry.key === key ? entry.value : readValue();

    const update = useCallback(
        (next: T) => {
            setEntry({ key, value: next });
            try {
                window.sessionStorage.setItem(key, JSON.stringify(next));
            } catch {
                // Storage can be full or blocked; the in-memory value is still correct.
            }
        },
        [key]
    );

    return [value, update];
};
